// src/components/pages/DisclaimerGate.js
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

/**
 * Bump VERSION to re-show after you change the disclaimer text.
 * Optionally change STORAGE_KEY_PREFIX to namespacing per app/env.
 */
const VERSION = "v1";
const STORAGE_KEY_PREFIX = "avistep.disclaimer.accepted";

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999; /* above map, side panels, etc. */
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Dialog = styled.div`
  width: min(560px, 92vw);
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  position: relative;
  padding: 14px;
  text-align: center;
  border: 5px solid var(--calcite-color-brand);
`;

const OkPill = styled.button`
  display: inline-block;
  margin: 20px auto 7px auto; /* centers it, adds spacing above and below */
  padding: 4px 16px;
  background: var(--calcite-color-brand);
  border: 1px solid black;
  border-radius: 5px;
  font-weight: 700;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  transition: background 0.15s ease;
  color: white;
  &:hover {
    background: #f9fafb; /* subtle hover, optional */
    color: var(--calcite-color-brand);
  }
`;

const Title = styled.div`
  font-weight: 700;
  font-size: 18px;
  margin-top: 4px; /* space under the pill */
  margin-bottom: 10px;
  color: black;
`;

const Body = styled.div`
  color: #374151; /* gray-700 */
  font-size: 14px;
  line-height: 1.6;
  text-align: left;
`;

function getKey() {
  return `${STORAGE_KEY_PREFIX}.${VERSION}`;
}

/**
 * Props:
 * - text (string or React node): custom body content
 * - title (string): defaults to "Disclaimer"
 * - version (string): optional override if you want to control version outside
 * - onAccept (function): optional callback when user clicks OK
 * - initiallyOpen (boolean): force open for testing (ignores storage)
 */
export function DisclaimerGate({
  text,
  title = "Disclaimer",
  version,
  onAccept,
  initiallyOpen = false,
}) {
  const storageKey = `${STORAGE_KEY_PREFIX}.${version || VERSION}`;
  const [open, setOpen] = useState(initiallyOpen);
  const okRef = useRef(null);

  useEffect(() => {
    if (initiallyOpen) return;
    try {
      const accepted = window.localStorage.getItem(storageKey) === "1";
      setOpen(!accepted);
    } catch {
      // If storage is blocked, just show the dialog; nothing else to do.
      setOpen(true);
    }
  }, [storageKey, initiallyOpen]);

  useEffect(() => {
    if (open && okRef.current) {
      okRef.current.focus();
    }
  }, [open]);

  const handleOk = () => {
    try {
      window.localStorage.setItem(storageKey, "1");
    } catch {
      /* ignore storage errors */
    }
    setOpen(false);
    if (onAccept) onAccept();
  };

  if (!open) return null;

  return (
    <Overlay role="dialog" aria-modal="true" aria-labelledby="dlg-title">
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Title id="dlg-title">{title}</Title>
        <Body>
          {text || (
            <>
              The geographical designations employed on this website and on the
              maps that are made available through it do not imply the
              expression of any opinion whatsoever on the part of BirdLife
              International, or contributory organisations, concerning the legal
              status of any country, territory or area, or concerning the
              delimitation of its frontiers or boundaries.
            </>
          )}
        </Body>
        <OkPill
          ref={okRef}
          onClick={handleOk}
          aria-label="Acknowledge disclaimer"
        >
          OK
        </OkPill>
      </Dialog>
    </Overlay>
  );
}

// also provide a default export to make importing flexible
export default DisclaimerGate;
